// Comprehensive logging system for frontend
const isDevelopment = process.env.NODE_ENV === 'development';

class Logger {
  constructor() {
    this.logs = [];
    this.sessionId = this.generateSessionId();
    this.startTime = new Date();

    // Batching configuration for backend submission
    this.batchSize = 50; // Send logs when batch reaches 50 entries
    this.batchInterval = 60000; // Or every 60 seconds
    this.pendingBatch = [];
    this.isFlushing = false; // Prevent concurrent flushes
    this.lastFlushTime = 0; // Debounce rapid flushes
    this.flushCooldown = 10000; // Minimum 10s between flushes
    // Token the backend rejected (401) for log posts. Idle tabs holding a dead
    // token used to post every minute forever; we now wait for a new token.
    this.rejectedToken = null;

    // Start batch timer
    this.batchTimer = setInterval(() => this.flushBatch(), this.batchInterval);

    // Send pending logs on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushBatch());
    }
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Removed localStorage methods - logs only go to file

  formatStackTrace(error) {
    if (!error.stack) return null;
    return error.stack.split('\n').slice(0, 5).join('\n');
  }

  log(level, category, message, data = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      sessionId: this.sessionId,
      level,
      category,
      message,
      data: this.sanitizeData(data),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Add stack trace for errors
    if (level === 'error' && data.error) {
      logEntry.stackTrace = this.formatStackTrace(data.error);
    }

    // Add to logs array for session only
    this.logs.push(logEntry);

    // Add to pending batch for backend submission
    this.pendingBatch.push({
      timestamp: logEntry.timestamp,
      level: logEntry.level,
      category: logEntry.category,
      message: logEntry.message,
      data: logEntry.data
    });

    // Auto-flush if batch is full (but respect cooldown to prevent flooding)
    if (this.pendingBatch.length >= this.batchSize) {
      const now = Date.now();
      if (now - this.lastFlushTime >= this.flushCooldown) {
        this.flushBatch();
      }
    }

    // ✅ INDUSTRY STANDARD: Conditional console output
    if (isDevelopment) {
      // Development: Show ALL logs in console with colors
      const styles = {
        debug: 'color: #6c757d',
        info: 'color: #0dcaf0',
        success: 'color: #198754',
        warn: 'color: #ffc107; font-weight: bold',
        error: 'color: #dc3545; font-weight: bold',
        system: 'color: #6f42c1; font-weight: bold'
      };

      const style = styles[level] || 'color: #000';
      const prefix = `[${level.toUpperCase()}] ${category}:`;

      if (level === 'error') {
        console.error(prefix, message, data);
      } else if (level === 'warn') {
        console.warn(prefix, message, data);
      } else {
        console.log(`%c${prefix}`, style, message, data);
      }
    } else {
      // Production: Only show ERROR and WARN (silent for info/debug/success)
      if (level === 'error') {
        console.error(`[ERROR] ${category}:`, message, this.sanitizeData(data));
      } else if (level === 'warn') {
        console.warn(`[WARN] ${category}:`, message);
      }
      // info, debug, success → SILENT in production ✅
    }

    // Send critical errors to backend (optional)
    if (level === 'error' && data.critical) {
      this.sendToBackend(logEntry);
    }
  }

  // Sanitize sensitive data (matches backend sanitization patterns)
  sanitizeData(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sanitized = { ...data };

    // Remove sensitive fields (matches backend patterns)
    const sensitiveFields = [
      'password', 'token', 'access_token', 'refresh_token',
      'email', 'phone_number', 'mobile_number', 'cvv', 'ssn',
      'credit_card', 'card_number', 'authorization'
    ];

    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Sanitize nested objects
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }

      // Mask email patterns in strings (matches backend: u***@domain.com)
      if (typeof sanitized[key] === 'string') {
        sanitized[key] = sanitized[key].replace(
          /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
          (match, user, domain) => {
            return `${user.charAt(0)}***@${domain}`;
          }
        );

        // Mask JWT tokens (matches backend: eyJ...)
        sanitized[key] = sanitized[key].replace(
          /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
          'eyJ...[REDACTED]'
        );
      }
    });

    return sanitized;
  }

  // Log levels with categories
  debug(category, message, data) {
    this.log('debug', category, message, data);
  }

  info(category, message, data) {
    this.log('info', category, message, data);
  }

  success(category, message, data) {
    this.log('success', category, message, data);
  }

  warn(category, message, data) {
    this.log('warn', category, message, data);
  }

  error(category, message, data) {
    this.log('error', category, message, data);
  }

  system(message, data) {
    this.log('system', 'SYSTEM', message, data);
  }

  // Track function execution
  trackFunction(category, functionName, fn, ...args) {
    const startTime = performance.now();
    const functionId = `${functionName}_${Date.now()}`;

    this.debug(category, `Function started: ${functionName}`, {
      functionId,
      args: args.length > 0 ? args : undefined
    });

    try {
      const result = fn(...args);
      
      // Handle promises
      if (result instanceof Promise) {
        return result
          .then(res => {
            const duration = performance.now() - startTime;
            this.success(category, `Function completed: ${functionName}`, {
              functionId,
              duration: `${duration.toFixed(2)}ms`,
              result: res
            });
            return res;
          })
          .catch(err => {
            const duration = performance.now() - startTime;
            this.error(category, `Function failed: ${functionName}`, {
              functionId,
              duration: `${duration.toFixed(2)}ms`,
              error: err,
              critical: true
            });
            throw err;
          });
      }

      // Handle synchronous functions
      const duration = performance.now() - startTime;
      this.success(category, `Function completed: ${functionName}`, {
        functionId,
        duration: `${duration.toFixed(2)}ms`,
        result
      });
      return result;

    } catch (err) {
      const duration = performance.now() - startTime;
      this.error(category, `Function failed: ${functionName}`, {
        functionId,
        duration: `${duration.toFixed(2)}ms`,
        error: err,
        critical: true
      });
      throw err;
    }
  }

  // Track API calls
  trackAPI(method, url, data, response, error = null) {
    const category = 'API';
    
    if (error) {
      this.error(category, `${method} ${url} failed`, {
        method,
        url,
        requestData: data,
        error: error.response?.data || error.message,
        status: error.response?.status
      });
    } else {
      this.success(category, `${method} ${url} success`, {
        method,
        url,
        requestData: data,
        responseData: response?.data,
        status: response?.status
      });
    }
  }

  // Track user actions
  trackAction(action, data) {
    this.info('USER_ACTION', action, data);
  }

  // Track page navigation
  trackNavigation(from, to) {
    this.info('NAVIGATION', `Navigate from ${from} to ${to}`, { from, to });
  }

  // Get logs with filters
  getLogs(filters = {}) {
    let filteredLogs = [...this.logs];

    if (filters.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filters.level);
    }

    if (filters.category) {
      filteredLogs = filteredLogs.filter(log => log.category === filters.category);
    }

    if (filters.startTime) {
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= new Date(filters.startTime));
    }

    if (filters.endTime) {
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= new Date(filters.endTime));
    }

    return filteredLogs;
  }

  // Get log statistics
  getStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {},
      byCategory: {},
      errors: [],
      sessionDuration: new Date() - this.startTime
    };

    this.logs.forEach(log => {
      // Count by level
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
      
      // Count by category
      stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
      
      // Collect errors
      if (log.level === 'error') {
        stats.errors.push({
          timestamp: log.timestamp,
          message: log.message,
          category: log.category
        });
      }
    });

    return stats;
  }

  // Clear logs
  clearLogs() {
    const confirmMsg = `Clear all ${this.logs.length} logs?`;
    if (window.confirm(confirmMsg)) {
      this.logs = [];
      localStorage.removeItem('frontend_logs');
      this.system('Logs cleared by user');
    }
  }

  // Export logs as JSON file
  exportLogs(filename = null) {
    const exportData = {
      sessionId: this.sessionId,
      exportTime: new Date().toISOString(),
      stats: this.getStats(),
      logs: this.logs
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFilename = filename || `frontend_logs_${new Date().toISOString().replace(/:/g, '-')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFilename);
    linkElement.click();

    this.info('SYSTEM', 'Logs exported', { filename: exportFilename, logCount: this.logs.length });
  }

  // True when there is a token and the backend has not already rejected it (401).
  // A new token (refresh / login / other tab) clears the block automatically.
  canSendWithToken() {
    const token = localStorage.getItem('access_token');
    return !!token && token !== this.rejectedToken;
  }

  // Remember the token that got a 401 so we stop re-posting with it.
  rememberIfRejected(error, sentToken) {
    if (error?.response?.status === 401) {
      this.rejectedToken = sentToken;
    }
  }

  // Flush pending logs to backend
  async flushBatch() {
    if (this.pendingBatch.length === 0) return;

    // Prevent concurrent flushes — a flush already in progress means
    // the network connection is occupied. Stacking flushes blocks
    // user-facing API requests (browsers limit ~6 connections per domain).
    if (this.isFlushing) return;

    // Don't flush if not authenticated, or the backend already rejected this token
    if (!this.canSendWithToken()) {
      // Cap batch size to prevent memory growth
      if (this.pendingBatch.length > 100) {
        this.pendingBatch = this.pendingBatch.slice(-50);
      }
      return;
    }

    // Cap batch to 50 entries max per flush to keep request small
    const batchToSend = this.pendingBatch.splice(0, 50);
    const sentToken = localStorage.getItem('access_token');
    this.isFlushing = true;
    this.lastFlushTime = Date.now();

    try {
      const apiModule = await import('../services/api');
      const api = apiModule.default;

      // Short timeout (5s) — log submission is non-critical
      // and must NEVER block user-facing requests
      await api.post('/admin/logs/frontend', batchToSend, {
        timeout: 5000
      });
      this.rejectedToken = null;

      if (isDevelopment) {
        console.log(`[LOGGER] Sent ${batchToSend.length} logs to backend`);
      }
    } catch (error) {
      this.rememberIfRejected(error, sentToken);
      // Silently fail — do NOT re-queue failed logs.
      // Re-queuing causes infinite loop: failed flush → error logged →
      // batch grows → next flush fails again → toast spam.
      if (isDevelopment) {
        console.error('[LOGGER] Failed to send logs:', error.message);
      }
    } finally {
      this.isFlushing = false;
    }
  }

  // Send critical error immediately (doesn't wait for batch)
  async sendToBackend(logEntry) {
    // Don't send if not authenticated / token rejected, or a flush is in progress
    if (!this.canSendWithToken()) return;
    if (this.isFlushing) return;
    const sentToken = localStorage.getItem('access_token');

    try {
      const apiModule = await import('../services/api');
      const api = apiModule.default;
      await api.post('/admin/logs/frontend', [logEntry], { timeout: 5000 });
    } catch (error) {
      this.rememberIfRejected(error, sentToken);
      // Silently fail to avoid infinite loop
      if (isDevelopment) {
        console.error('[LOGGER] Failed to send critical error:', error.message);
      }
    }
  }

  // Cleanup method for unmounting
  destroy() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    this.flushBatch(); // Send remaining logs
  }

  // Create a formatted log report
  generateReport() {
    const stats = this.getStats();
    const errors = this.getLogs({ level: 'error' });
    
    const report = {
      summary: {
        sessionId: this.sessionId,
        startTime: this.startTime,
        duration: `${(stats.sessionDuration / 1000 / 60).toFixed(2)} minutes`,
        totalLogs: stats.total,
        levels: stats.byLevel,
        categories: stats.byCategory
      },
      recentErrors: errors.slice(-10),
      performance: this.logs
        .filter(log => log.data?.duration)
        .map(log => ({
          function: log.message,
          duration: log.data.duration,
          timestamp: log.timestamp
        }))
    };

    return report;
  }
}

// Create singleton instance
const logger = new Logger();

// Logger is private - not exposed to window

export default logger;